import { zodResolver } from '@hookform/resolvers/zod';
import { Close, Description } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { formatDistance } from 'date-fns';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useSWRConfig } from 'swr';
import { z } from 'zod';

import { UserContext } from '~/auth/userContext';
import AttachmentUploader from '~/components/AttachmentUploader';
import { formatLocaleDateNumeric } from '~/components/compliance/formatters';
import { revalidateComplianceScreen } from '~/components/compliance/revalidateComplianceScreen';
import type { FieldDef } from '~/components/form/utils';
import { createFormInvalidHandler, FieldAndCell } from '~/components/form/utils';
import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';
import { violationsResolveCreate } from '~/data/generated/endpoints/violations/violations';
import type { ViolationResolveRequest } from '~/data/generated/model';
import { Attachment } from '~/data/schemas/attachment';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

import type { Violation } from './violationsFetcher';

// Update schema to match new API requirements
const resolveViolationSchema = z.object({
  resolutionType: z.enum(['reinspection', 'waiver', 'affidavit', 'other']),
  customResolutionType: z.string().optional(),
  resolutionComment: z.string().min(1, 'Resolution comment is required'),
});

type ResolveViolationFormData = z.infer<typeof resolveViolationSchema>;

const customResolutionField: FieldDef = {
  name: 'customResolutionType',
  label: 'Custom Resolution Type',
  type: 'text',
  required: true,
  gridProps: { xs: 12 },
  fieldProps: {
    placeholder: 'Enter custom resolution type...',
  },
};

// Form field definitions
const baseResolutionFields: FieldDef[] = [
  {
    name: 'resolutionType',
    label: 'Resolution Type',
    type: 'select',
    required: true,
    options: [
      { id: 'reinspection', label: 'Resolved by Reinspection' },
      { id: 'affidavit', label: 'Resolved by Affidavit' },
      { id: 'waiver', label: 'Resolved by Waiver' },
      { id: 'other', label: 'Other' },
    ],
    gridProps: { xs: 12 },
  },
  {
    name: 'resolutionComment',
    label: 'Resolution Comment',
    type: 'text-multiline',
    required: true,
    gridProps: { xs: 12 },
    fieldProps: {
      multiline: true,
      rows: 3,
      inputProps: { maxLength: 500 },
      placeholder: 'Add resolution details here...',
    },
  },
];

interface ResolveViolationDialogProps {
  open: boolean;
  onClose: () => void;
  violation: Violation;
  certificateCarName: string;
  certificateCarId: string;
}

const AttachmentPreview = ({ attachment }: { attachment: Attachment }) => (
  <Box sx={{ backgroundColor: '#1976d214', padding: 2, marginTop: 2 }}>
    <Box sx={{ display: 'flex', gap: '20px' }}>
      <Description sx={{ marginTop: '8px' }} />
      <Box>
        <Typography variant="body2">{attachment.name}</Typography>
        <Typography variant="caption">PDF Document</Typography>
      </Box>
    </Box>
  </Box>
);

const ResolveViolationDialog = ({
  open,
  onClose,
  violation,
  certificateCarName,
  certificateCarId,
}: ResolveViolationDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { locale } = useContext(UserContext);
  const terms = useLocaleTerms();
  // The cache-scoped mutate, not the module-level one exported from `swr`: the
  // global mutate is bound to the default cache and silently does nothing under
  // an `SWRConfig` that supplies its own provider.
  const { mutate } = useSWRConfig();
  const { showSnackbar } = useLAISnackbar();
  const pillSx = BUTTON_SX;
  const { refresh: refreshActionItems } = useActionItemsRefresh();
  const [uploadedAttachment, setUploadedAttachment] = useState<Attachment | null>(null);
  const [showCustomField, setShowCustomField] = useState(false);

  const { control, handleSubmit, reset } = useForm<ResolveViolationFormData>({
    resolver: zodResolver(resolveViolationSchema),
    defaultValues: {
      resolutionType: 'reinspection',
      resolutionComment: '',
      customResolutionType: '',
    },
  });

  const resolutionType = useWatch({ control, name: 'resolutionType' });

  useEffect(() => {
    setShowCustomField(resolutionType === 'other');
  }, [resolutionType]);

  const onFilesUploaded = useCallback((attachments: Attachment[]) => {
    if (attachments.length > 0) {
      setUploadedAttachment(attachments[0]);
    }
  }, []);

  const handleResolve = async (data: ResolveViolationFormData) => {
    try {
      setIsSubmitting(true);

      const requestData: ViolationResolveRequest = {
        resolutionNotes: data.resolutionComment,
        resolutionType: data.resolutionType,
        customResolutionType:
          data.resolutionType === 'other' ? data.customResolutionType : undefined,
        documentId: uploadedAttachment?.id ?? null,
      };

      await violationsResolveCreate(violation.id, requestData);

      // Resolving a defect changes every count on the compliance screen, but
      // this handler used to invalidate only the two keys the panel itself
      // reads. The tiles, the donut, the upcoming-due histogram and the grid
      // row's open-defect count all sat on other keys whose background
      // revalidation is deliberately off, so they kept pre-resolve numbers
      // until a full page reload — the server said 6 while the screen said 7.
      // The full dependent set, and why each key is in it, lives with the
      // helper. Awaited rather than fire-and-forget so the dialog cannot close
      // over a half-applied refresh. (CX-416)
      await revalidateComplianceScreen(mutate, certificateCarId);
      // The backend signal removes the violation.overdue Backlog item on
      // resolve; tell the Backlog list + badge to re-fetch.
      refreshActionItems();

      showSnackbar(`${terms.violation} resolved successfully`, { variant: 'success' });
      reset();
      onClose();
    } catch (error) {
      console.error('Error resolving violation:', error);
      showSnackbar(`Failed to resolve ${terms.violation.toLowerCase()}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDate = new Date(violation.createdAt);
  const openedDaysAgo = formatDistance(openDate, new Date(), { addSuffix: true });

  const resolutionFields = [...baseResolutionFields];
  if (showCustomField) {
    resolutionFields.splice(1, 0, customResolutionField);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="end" sx={{ justifyContent: 'end' }}>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          alignItems="center"
          sx={{ justifyContent: 'center', marginTop: '5px', marginBottom: '25px' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '20px' }}>
            How was this {terms.violation.toLowerCase()} resolved?
          </Typography>
        </Box>
        <form
          onSubmit={handleSubmit(
            handleResolve,
            createFormInvalidHandler<ResolveViolationFormData>({
              formName: 'ResolveViolationDialog',
              showSnackbar,
            }),
          )}
        >
          <Stack spacing={3}>
            {/* Info Box */}
            <Box
              sx={{
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                p: 1,
                // A 4px box inside a rounded dialog full of pill buttons reads
                // as two design systems; the panels share one radius.
                borderRadius: `${SURFACE_RADIUS}px`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#00000099', fontSize: '13px', fontWeight: 400 }}
              >
                Date Open: {formatLocaleDateNumeric(violation.createdAt, locale)} ({openedDaysAgo})
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ lineHeight: '22px', fontSize: '13px', textTransform: 'capitalize' }}
              >
                {violation.responsibility} | {violation.location} | {violation.code}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#00000099', fontSize: '13px', fontWeight: 400 }}
              >
                {violation.description}
              </Typography>
            </Box>

            {/* Resolution Form */}
            <Box>
              <Grid container spacing={2}>
                {resolutionFields.map((field) => (
                  <FieldAndCell key={field.name} field={field} control={control} />
                ))}
              </Grid>
            </Box>

            {/* Attachment Uploader */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Correction Affidavit (Optional)
              </Typography>
              <AttachmentUploader
                fileSizeLimit={5}
                acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
                acceptedFileTypesLabel="PDF"
                onFilesUploaded={onFilesUploaded}
              />

              {uploadedAttachment ? <AttachmentPreview attachment={uploadedAttachment} /> : null}
            </Box>

            {/* Submit Button */}
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" type="submit" disabled={isSubmitting} sx={pillSx}>
                Resolve {terms.violation}
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResolveViolationDialog;
